<template>
  <div class="advanced-team-picker-row" :class="elClasses">
    <div
      ref="parentWrapper"
      class="advanced-team-picker-row__parent-wrapper"
      :data-id="team.id"
      :data-from-parent="team.parentId"
      @click="expand"
    >
      <Icon v-if="!forDisplayOnly" :class="{ expanded }" :style="expanderStyle">
        {{ arrowDownLine }}
      </Icon>
      <div v-if="forDisplayOnly" class="team-name v-centerer">
        <span ref="teamName">{{ teamChecbkboxText }}</span>
      </div>
      <Checkbox
        v-else
        v-model="teamSelected"
        :title="teamChecbkboxText"
        class="team-name"
        stop-propagation
        no-animation
        :disabled="isDisabled"
      />
      <div v-if="!checkable" class="additional-text aa">
        {{ $t('message.NO_ACCESS_TO_TEAM') }}
      </div>
      <div v-else-if="searchMatches && searchMatches.length" class="additional-text bb">
        <span v-for="(match, idx) in searchMatches" :key="idx" class="search-match">
          {{ match }}
        </span>
      </div>
      <Checkbox
        v-if="teamObj.teams.length && !forDisplayOnly"
        v-model="allChildrenSelected"
        class="field-hint advanced-team-picker-row__children-cb"
        :title="min ? $t('message.SELECT_CHILDREN_TEAMS_SHORT') : $t('message.SELECT_CHILDREN_TEAMS')"
        stop-propagation
        no-animation
      />
    </div>
    <TransitionExpand>
      <div
        v-if="expanded && teamObj.teams"
        class="advanced-team-picker-row__children-wrapper"
        :class="childrenWrapperClasses"
      >
        <AdvancedTeamPickerRow
          v-for="childTeam in teamObj.teams.slice(0, teamsDisplayedRange.value)"
          :key="`${parentId}-${teamObj.id}-${childTeam.id}`"
          :team="childTeam"
          :parent-id="`${parentId}-${teamObj.id}`"
          :checkable="checkable"
          :closeable="closeable"
          :for-display-only="forDisplayOnly"
          :allow-change-hierarchy="allowChangeHierarchy"
          :min="min"
          :disable-higher-hierarchy="disableHigherHierarchy"
          :disable-inaccessible="disableInaccessible"
          :config="config"
          @edit="$emit('edit', $event)"
          @hierarchyChange="(e) => { $emit('hierarchyChange', e) }"
        />
        <div
          v-if="teamsDisplayedRange.show && teamObj.teams.length"
          class="advanced-team-picker-row__children-wrapper__show-more-teams"
          @click="displayOthersTeams"
        >
          {{ $t('message.LOAD_MORE') }}
        </div>
        <div class="tree-level" />
      </div>
    </TransitionExpand>
  </div>
</template>

<script>
/**
 * Row of the Advanced team picker. Which may contain other AdvancedTeamPickerRow as children
 *
 * @vue-prop {Object} team - Team object with a 'teams' and a 'selected' property*
 * @vue-prop {Number} parentId - ID of the parent team of the team prop. (used only in v-for key)
 * @vue-prop {Boolean} checkable - If false, this team and its children cannot be checked by this user
 * @vue-prop {Boolean} closeable - If false, the item cannot be closed
 * @vue-prop {Array} searchMatches - Visual indicator when a search is made so the user knows why it matches
 * @vue-prop {Boolean} forDisplayOnly - If true, remove all checkboxes and expand arrows
 * @vue-prop {Boolean} allowChangeHierarchy - If true and forDisplayOnly true, allows reorganizing the teams hierarchy
 * @vue-prop {Boolean} min - Display as a minimal view
 * @vue-prop {Boolean} disableHigherHierarchy - Teams with a higher hierarchy level cannot be selected
 * @vue-prop {Boolean} disableInaccessible - Inaccessible cannot be selected. A team is accessible if it has an
 *                                              access throuh the hierarchy tree or through user accesses
 * @vue-prop {Boolean} noDefaultExpand - Disable default expansion, to prevent mounting children
 *
 * @vue-event {Object|Array} Team object or array of teams that have been edited
 * @vue-event {Object} hierarchyChange - Emitted when the user reorganises the teams in the tree
 *                                       Emits an object with 3 prperties :
 *                                       * childTeamId: Team being moved
 *                                       * parentTeamId: New parent team
 *                                       * fromParentTeamId: Old parent team
 */

import $ from 'jquery';
import { cloneDeep } from 'lodash';
// Components
import Checkbox from '@/components/checkbox.vue';
import Icon from '@/components/icon.vue';
import TransitionExpand from '@/components/transition-expand.vue';
// Utils
import { calert, decodeHTML } from '@/utils/main';
import { arrowDownLine } from '@/assets/icons';
import { ADV_TEAM_PICKER_INITIAL_VALUES } from '@/utils/advanced-team-picker-utils';

export default {

  name: 'AdvancedTeamPickerRow',

  components: {
    Checkbox,
    Icon,
    TransitionExpand,
  },

  props: {
    team: {
      type: Object,
      required: true,
    },
    parentId: {
      type: String,
      default: '',
    },
    checkable: {
      type: Boolean,
      default: true,
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    searchMatches: {
      type: Array,
      default: () => [],
    },
    forDisplayOnly: Boolean,
    allowChangeHierarchy: Boolean,
    min: Boolean,
    disableHigherHierarchy: Boolean,
    disableInaccessible: Boolean,
    config: Boolean,
    noDefaultExpand: Boolean,
  },

  data() {
    return {
      arrowDownLine,
      expanded: !this.noDefaultExpand,
      teamObj: null,
      teamsDisplayedRange: {
        value: ADV_TEAM_PICKER_INITIAL_VALUES,
        show: true,
      },
    };
  },

  computed: {
    /** Boolean: Whether this item is disabled */
    isDisabled() {
      if (this.config) {
        return false;
      }
      const hierarchyDisable = this.disableHigherHierarchy
        && this.teamObj.hierarchyLevel > this.currentTeam.hierarchyLevel;
      const userIds = (this.currentTeam.users || []).map(user => user.id);
      const accessDisable = this.disableInaccessible
        && !this.teamObj.parents.some(id => id === this.currentTeam.id)
        && !this.teamObj.accesses.some(userId => userIds.includes(userId));
      return hierarchyDisable || accessDisable;
    },
    /** Boolean: teamObj 'selected' property handler */
    teamSelected: {
      get() {
        return this.teamObj.selected;
      },
      set(selected) {
        this.$emit('edit', { ...this.teamObj, selected });
      },
    },
    /** Boolean: True if all the teams in the 'teams' array are selected */
    allChildrenSelected: {
      get() {
        return !this.teamObj.teams.some(team => !team.selected);
      },
      set(selected) {
        this.teamObj.teams.forEach((team) => {
          team.selected = selected;
        });
        this.$emit('edit', this.teamObj.teams);
      },
    },
    /** String: text displayed to the right of the selection checkbox */
    teamChecbkboxText() {
      return decodeHTML(this.team.name) + ' ' + this.team.id;
    },
    additionalText() {
      const addText = this.checkable ? '' : this.$t('message.NO_ACCESS_TO_TEAM');
      return addText;
    },
    /** Object: CSS style applied to the expander arrow */
    expanderStyle() {
      const { teams } = this.teamObj;
      return {
        visibility: !teams || !teams.length ? 'hidden' : undefined,
      };
    },
    /** Object: CSS classes applied to this.$el */
    elClasses() {
      return {
        'advanced-team-picker-row--min': this.min,
        'advanced-team-picker-row--disabled': this.isDisabled,
      };
    },
    /** Object: CSS classes applied to the children wrapper section */
    childrenWrapperClasses() {
      return {
        // NOTE: These classes are used by advanced-team-picker-tree as well
        'advanced-team-picker-row__children-wrapper--min': this.min,
        'advanced-team-picker-row__children-wrapper--for-display': this.forDisplayOnly,
      };
    },
    /** Array: All teams from the teams tree */
    allTeams() {
      return this.$store.getters.allTeamsTreeFlattened;
    },
  },

  watch: {
    team: {
      deep: true,
      handler() {
        this.teamObj = cloneDeep(this.team);
        this.teamObj.teams = this.teamObj.teams || [];
      },
    },
  },

  created() {
    this.teamObj = cloneDeep(this.team);
    this.teamObj.teams = this.teamObj.teams || [];
    if (this.teamsDisplayedRange.value >= this.teamObj.teams.length) {
      this.teamsDisplayedRange.show = false;
    }
  },

  mounted() {
    if (this.allowChangeHierarchy) {
      this.setupDragAndDrop();
    }
  },

  beforeDestroy() {
    if (this.allowChangeHierarchy) {
      $(this.$refs.parentWrapper).draggable('destroy');
      $(this.$el).droppable('destroy');
    }
  },

  methods: {
    decodeHTML,
    /** Hide/Show childrens content */
    expand() {
      if (this.closeable && !this.forDisplayOnly) {
        this.expanded = !this.expanded;
      }
    },
    /** Setup the drag and drop behaviour to reorganise the teams hierarchy */
    setupDragAndDrop() {
      $(this.$refs.parentWrapper).draggable({
        helper: 'clone',
        distance: 10,
        addClasses: false,
        revert: 'invalid',
        refreshPositions: true,
        create: (e) => {
          $(e.target).draggable('option', 'cursorAt', {
            left: this.$refs.teamName.clientWidth / 2,
            top: e.target.clientHeight / 2,
          });
        },
      });

      $(this.$el).droppable({
        accept: '.advanced-team-picker-row__parent-wrapper',
        tolerance: 'pointer',
        greedy: true,
        drop: (e, ui) => {
          // ID of the team dragged
          const childTeamId = Number(ui.helper[0].dataset.id);
          // ID of the parent team that this team was dragged from
          const fromParentTeamId = Number(ui.helper[0].dataset.fromParent);
          const parentTeamId = this.team.id;

          // Check the redundancy errors
          const childTeam = this.allTeams.find(team => team.id === childTeamId);
          if (childTeam) {
            if (childTeamId === parentTeamId) {
              return;
            }
            const directSup = childTeam.supervisedTeamIds.map(teamId => this.allTeams.find(t => t.id === teamId));
            const allSupervised = directSup.flatMap(team => team.children || []).concat(childTeam.supervisedTeamIds);
            if (allSupervised.includes(parentTeamId)) {
              calert(this.$t('message.SUPERVISOR_TEAM_ERROR', this.team));
              return;
            }

            // Emit the change
            this.$emit('hierarchyChange', { childTeamId, parentTeamId, fromParentTeamId });
          }
        },
      });
    },
    /** Display more teams */
    displayOthersTeams() {
      this.teamsDisplayedRange.value += ADV_TEAM_PICKER_INITIAL_VALUES;
      if (this.teamsDisplayedRange.value >= this.teamObj.teams.length) {
        this.teamsDisplayedRange.show = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.advanced-team-picker-row {
  $bg-color: rgba(99, 56, 193, 0.05);
  $arrowPadding: 0.5em;
  // background: $bg-color;
  // box-shadow: 0px 0px 2px $bg-color;

  &__parent-wrapper {
    cursor: pointer;
    display: flex;
    align-items: center;
    height: 2.3em;
    padding: 0px $arrowPadding;
    box-sizing: border-box;

    &:hover {
      background: $color-hoverbtn-light-blue;
    }
    svg.icon {
      width: 0.8em;
      margin-right: $arrowPadding;
      transform: rotate(-90deg);
      transition: transform 0.15s linear;
      flex: 0 0 auto;

      &.expanded {
        transform: rotate(0deg);
      }
    }
    .team-name {
      flex: 1;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;

      span {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .checkbox__title {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }
    .advanced-team-picker-row__children-cb {
      flex: 0 0 auto;
    }
    .additional-text {
      flex: 1;
      margin: 0px 10px;
      font-style: italic;
      font-size: 0.9em;
      overflow: hidden;
      white-space: nowrap;

      .search-match {
        vertical-align: middle;
        background: #00ffff;
        padding: 2px 5px;
        margin-left: 5px;
      }
    }

    &.draggable-active {
      z-index: 2;
    }
  }

  // NOTE: These classes are used by advanced-team-picker-tree as well
  &__children-wrapper {
    position: relative;
    padding-left: 1.9em;

    &:before {
      content: " ";
      position: absolute;
      top: 0px;
      bottom: 0px;
      left: calc(0.9em - 1px);
      width: 2px;
      background: rgba(0, 0, 0, 0.1);
    }

    &--min {
      padding-left: 1.5em;

      &:before {
        content: " ";
        left: calc(0.7em - 1px);
      }
    }

    &--min#{&}--for-display {
      padding-left: 1.1em;

      &:before {
        content: " ";
        left: calc(0.5em - 1px);
      }
    }
    &__show-more-teams {
      text-align: center;
      margin: 5px 0px 15px 25px;
      padding: 0px 6px;
      width: 100px;
      border-radius: 4px;
      background-color: #dfdfdf;
      font-size: 12px;
      box-shadow: 1px 1.5px 2px rgba(140, 138, 138, 0.8);
      transition: .2s;
      &:hover {
        color: rgb(111, 110, 110);
        background-color:  #c2c2c2e4;
        box-shadow: 1px 1.5px 2px rgba(131, 131, 131, 0.8);
        cursor: pointer;
      }
    }
  }
  .checkbox.checked .tick img {
    width: 1em;
    height: 1em;
  }

  &--min {
    $arrowPadding: 0.3em;

    .advanced-team-picker-row__parent-wrapper {
      padding: 0px $arrowPadding;

      svg.icon {
        margin-right: $arrowPadding;
      }
    }
  }

  &--disabled {
    & > .parent-wrapper {
      opacity: 0.9;
      cursor: not-allowed;
    }
  }

  &.ui-droppable-active {

    > .advanced-team-picker-row__parent-wrapper {
      border: 2px dashed $color-droppable-borders;
    }

    &.ui-droppable-hover {
      > .advanced-team-picker-row__parent-wrapper {
        background: $color-droppable-active;
      }
    }
  }
}

</style>
